import { PushNotifications, Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import axiosInstance from '../config/axiosInstance';
import { toast } from 'react-hot-toast';

/**
 * Service de gestion des Push Notifications
 * Gère l'enregistrement, les permissions et la réception des notifications
 */
class PushNotificationService {
  private token: string | null = null;

  /**
   * Initialise le service de push notifications
   * À appeler au démarrage de l'application
   */
  async initialize(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      this.setupListeners();
      await this.requestPermission();
      await this.register();
    } catch (error) {
      console.error('Erreur initialisation push notifications:', error);
      throw error;
    }
  }

  /**
   * Demande la permission d'envoyer des notifications
   * Sur iOS: popup système
   * Sur Android: auto-accordée (avant Android 13)
   */
  private async requestPermission(): Promise<void> {
    const result = await PushNotifications.requestPermissions();

    if (result.receive !== 'granted') {
      throw new Error('Permission refusée par l\'utilisateur');
    }
  }

  /**
   * Enregistre l'appareil pour recevoir des notifications
   * Génère un token unique pour cet appareil
   */
  private async register(): Promise<void> {
    await PushNotifications.register();
  }

  /**
   * Configure tous les listeners d'événements
   */
  private setupListeners(): void {
    console.log('🔧 Configuration des listeners de push notifications...');

    PushNotifications.addListener('registration', async (token: Token) => {
      console.log('📱 Token reçu (Capacitor):', token.value);
      this.token = token.value;
      await this.sendTokenToBackend(token.value);
    });

    PushNotifications.addListener('registrationError', (error: any) => {
      console.error('❌ Erreur enregistrement push:', error);
      toast.error('Impossible d\'activer les notifications');
    });

    PushNotifications.addListener(
      'pushNotificationReceived',
      (notification: PushNotificationSchema) => {
        console.log('📬 Notification reçue (app ouverte):', notification);
        this.handleNotificationReceived(notification);
      }
    );

    PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (action: ActionPerformed) => {
        console.log('👆 Notification cliquée:', action);
        this.handleNotificationClick(action);
      }
    );

    console.log('✅ Listeners configurés avec succès');
  }

  /**
   * Envoie le token au backend Laravel
   * @param token - Token push (APNs ou FCM)
   */
  private async sendTokenToBackend(token: string): Promise<void> {
    try {
      const platform = Capacitor.getPlatform();

      const response = await axiosInstance.post('/device-tokens', {
        token: token,
        platform: platform,
        device_model: await this.getDeviceModel(),
        os_version: await this.getOSVersion(),
        app_version: await this.getAppVersion(),
      });

      localStorage.setItem('push_token', token);
    } catch (error: any) {
      console.error('Erreur envoi token:', error.response?.status || error.message);

      if (error.response?.status === 401) {
        localStorage.setItem('push_token_pending', token);
      }
    }
  }

  /**
   * Gère la réception d'une notification quand l'app est ouverte
   * @param notification - Données de la notification
   */
  private handleNotificationReceived(notification: PushNotificationSchema): void {
    // Afficher un toast avec le contenu de la notification
    const message = `${notification.title}\n${notification.body}`;
    toast.success(message, {
      duration: 5000,
      position: 'top-right',
    });
  }

  /**
   * Gère le clic sur une notification
   * Navigation vers le contenu approprié
   * @param action - Action performée
   */
  private handleNotificationClick(action: ActionPerformed): void {
    // Sauvegarder la demande de navigation
    localStorage.setItem('pending_notification_navigation', '/notifications');
  }

  /**
   * Récupère le modèle de l'appareil
   */
  private async getDeviceModel(): Promise<string> {
    try {
      // Peut être amélioré avec @capacitor/device
      const platform = Capacitor.getPlatform();
      return platform === 'ios' ? 'iPhone' : 'Android Device';
    } catch {
      return 'Unknown';
    }
  }

  /**
   * Récupère la version de l'OS
   */
  private async getOSVersion(): Promise<string> {
    try {
      // Peut être amélioré avec @capacitor/device
      return 'Unknown';
    } catch {
      return 'Unknown';
    }
  }

  /**
   * Récupère la version de l'app
   */
  private async getAppVersion(): Promise<string> {
    try {
      // Peut être amélioré avec @capacitor/app
      return '1.0.0';
    } catch {
      return '1.0.0';
    }
  }

  /**
   * Supprime le token de l'appareil (lors de la déconnexion)
   */
  async removeToken(): Promise<void> {
    const token = this.token || localStorage.getItem('push_token');

    if (!token) {
      return;
    }

    try {
      await axiosInstance.delete(`/device-tokens/${token}`);
      localStorage.removeItem('push_token');
      localStorage.removeItem('push_token_pending');
      this.token = null;
    } catch (error) {
      console.error('Erreur suppression token:', error);
    }
  }

  /**
   * Envoie un token en attente après connexion
   */
  async sendPendingToken(): Promise<void> {
    const pendingToken = localStorage.getItem('push_token_pending');

    if (pendingToken) {
      await this.sendTokenToBackend(pendingToken);
      localStorage.removeItem('push_token_pending');
    }
  }

  /**
   * Récupère les notifications en attente (badge)
   */
  async getPendingNotifications(): Promise<void> {
    try {
      await PushNotifications.getDeliveredNotifications();
    } catch (error) {
      console.error('Erreur récupération notifications:', error);
    }
  }

  /**
   * Supprime toutes les notifications de la barre de notification
   */
  async removeAllDeliveredNotifications(): Promise<void> {
    try {
      await PushNotifications.removeAllDeliveredNotifications();
    } catch (error) {
      console.error('Erreur suppression notifications:', error);
    }
  }

  /**
   * Vérifie si les notifications sont supportées
   */
  isSupported(): boolean {
    return Capacitor.isNativePlatform();
  }

  /**
   * Récupère le token actuel
   */
  getToken(): string | null {
    return this.token || localStorage.getItem('push_token');
  }
}

// Export singleton
export const pushNotificationService = new PushNotificationService();
export default pushNotificationService;

